export interface ParsedApiError {
  title: string;
  message: string;
  actionText?: string;
  source?: string;
  category?: string;
  severity?: 'info' | 'warning' | 'error';
  retryable?: boolean;
  details: string[];
  metaCode?: string | number | null;
  metaSubcode?: string | number | null;
  metaType?: string | null;
  fbtraceId?: string | null;
}

export function parseApiError(err: any, fallback: string, title = 'No se pudo completar la acción'): ParsedApiError {
  const body = resolveErrorBody(err);
  const rawMessage = resolveMessage(body, err, fallback);
  const presentation = humanizeMessage(rawMessage, body, err);
  const category = resolveCategory(body, rawMessage, presentation.category);

  return {
    title: humanizeTitle(title, category),
    message: presentation.message,
    actionText: presentation.actionText,
    source: typeof body?.source === 'string' ? body.source : 'BACKEND',
    category,
    severity: presentation.severity,
    retryable: typeof body?.retryable === 'boolean' ? body.retryable : undefined,
    details: resolveDetails(body),
    metaCode: body?.metaCode,
    metaSubcode: body?.metaSubcode,
    metaType: body?.metaType,
    fbtraceId: body?.fbtraceId
  };
}

interface MessagePresentation {
  message: string;
  actionText?: string;
  category?: string;
  severity: 'info' | 'warning' | 'error';
}

function resolveErrorBody(err: any): any {
  if (err?.error !== undefined) {
    return err.error;
  }
  return err;
}

function resolveMessage(body: any, err: any, fallback: string): string {
  if (typeof body === 'string' && body.trim() && !isGenericHttpMessage(body)) {
    return body.trim();
  }

  if (typeof body?.message === 'string' && body.message.trim()) {
    return body.message.trim();
  }

  if (typeof body?.detail === 'string' && body.detail.trim()) {
    return body.detail.trim();
  }

  const errorsMessage = getFirstErrorsMessage(body?.errors);
  if (errorsMessage) {
    return errorsMessage;
  }

  if (typeof body?.violations === 'object') {
    const violationsMessage = getFirstErrorsMessage(body.violations);
    if (violationsMessage) {
      return violationsMessage;
    }
  }

  if (typeof body?.error === 'string' && body.error.trim() && !isGenericHttpMessage(body.error)) {
    return body.error.trim();
  }

  if (typeof err?.message === 'string' && err.message.trim() && !isGenericHttpMessage(err.message)) {
    return err.message.trim();
  }

  return fallback;
}

function isGenericHttpMessage(message: string): boolean {
  return /^(bad request|unauthorized|forbidden|not found|internal server error|service unavailable)$/i.test(
    message.trim(),
  ) || /^http failure response for .*:\s*\d+\s+[^:]+$/i.test(message.trim());
}

function humanizeMessage(message: string, body: any, err: any): MessagePresentation {
  const raw = String(message || '').trim();
  const normalized = raw.replace(/^Bloqueo Estricto\s*:\s*/i, '').trim();
  const code = String(body?.code || '').toUpperCase();

  if (code === 'GESTION_TICKET_CREDENTIALS_INVALID') {
    return {
      message: 'El usuario o la contraseña de Gestión Ticket son incorrectos.',
      category: code,
      severity: 'error',
    };
  }

  if (code === 'CUSTOMER_REPLY_REQUIRED' || /esperando respuesta del cliente|cliente aún no habilitó/i.test(normalized)) {
    return {
      message: 'No se puede enviar el mensaje todavía.',
      actionText: 'El cliente tiene que responder antes de que puedas continuar la conversación. Esperá su respuesta y volvé a intentar.',
      category: 'CUSTOMER_REPLY_REQUIRED',
      severity: 'warning',
    };
  }

  if (code === 'TEMPLATE_REQUIRED' || /ventana.*Meta.*vencida|Meta requiere enviar una plantilla/i.test(normalized)) {
    return {
      message: 'No se puede enviar el mensaje todavía.',
      actionText: 'Para continuar, elegí una plantilla aprobada y enviala al cliente.',
      category: 'TEMPLATE_REQUIRED',
      severity: 'warning',
    };
  }

  if (/siendo gestionada por el bot|menú o mensaje pendiente activo/i.test(normalized)) {
    return {
      message: 'No se puede enviar el mensaje todavía.',
      actionText: 'La conversación está siendo gestionada automáticamente. Esperá a que termine esa gestión y volvé a intentar.',
      category: 'CONVERSATION_BLOCKED',
      severity: 'warning',
    };
  }

  if (/conversación está cerrada|conversacion está cerrada/i.test(normalized)) {
    return {
      message: 'La conversación está cerrada.',
      actionText: 'No se pueden enviar nuevos mensajes en una conversación cerrada.',
      category: 'CONVERSATION_CLOSED',
      severity: 'warning',
    };
  }

  if (code === 'CONVERSATION_OWNED_BY_OTHER') {
    return {
      message: normalized,
      actionText: 'Si necesitás intervenir, revisá la conversación y confirmá el envío cuando corresponda.',
      category: code,
      severity: 'warning',
    };
  }

  if (/^the template does not exist\.?$/i.test(normalized)) {
    return {
      message: 'La plantilla seleccionada no existe o ya no está disponible.',
      actionText: 'Elegí otra plantilla aprobada y volvé a intentar.',
      severity: 'error',
    };
  }

  if (err?.status === 0 || /network error|failed to fetch|load failed/i.test(normalized)) {
    return {
      message: 'No pudimos conectarnos al sistema.',
      actionText: 'Revisá tu conexión e intentá de nuevo en unos segundos.',
      category: 'CONNECTION_ERROR',
      severity: 'error',
    };
  }

  if (err?.status === 502 || err?.status === 503) {
    return {
      message: 'El servicio está temporalmente no disponible.',
      actionText: 'Esperá unos segundos y volvé a intentar.',
      category: 'SERVICE_UNAVAILABLE',
      severity: 'error',
    };
  }

  return {
    message: normalized || 'No se pudo completar la acción.',
    severity: 'error',
  };
}

function resolveCategory(body: any, rawMessage: string, detectedCategory?: string): string | undefined {
  if (typeof body?.category === 'string' && body.category.trim()) {
    return body.category.trim().toUpperCase();
  }
  if (typeof body?.code === 'string' && body.code.trim()) {
    return body.code.trim().toUpperCase();
  }
  if (detectedCategory) {
    return detectedCategory;
  }
  if (/Bloqueo Estricto|siendo gestionada por el bot|menú o mensaje pendiente activo/i.test(rawMessage)) {
    return 'CONVERSATION_BLOCKED';
  }
  if (/conversación está cerrada|conversacion está cerrada/i.test(rawMessage)) {
    return 'CONVERSATION_CLOSED';
  }
  return undefined;
}

function humanizeTitle(title: string, category?: string): string {
  const normalized = String(title || '').trim();
  const upperCategory = String(category || '').toUpperCase();

  if (upperCategory === 'CONVERSATION_BLOCKED') return 'Envío bloqueado';
  if (upperCategory === 'CONVERSATION_CLOSED') return 'Conversación cerrada';
  if (upperCategory === 'CUSTOMER_REPLY_REQUIRED') return 'Esperando respuesta del cliente';
  if (upperCategory === 'TEMPLATE_REQUIRED') return 'Se necesita una plantilla';
  if (!normalized || /^error( de api)?$/i.test(normalized)) return 'No se pudo completar la acción';
  if (/^error de api$/i.test(normalized)) return 'No se pudo completar la acción';
  return normalized;
}

function getFirstErrorsMessage(errors: unknown): string | null {
  if (Array.isArray(errors)) {
    const firstArrayMessage = errors.find((item: unknown) => typeof item === 'string' && item.trim());
    return typeof firstArrayMessage === 'string' ? firstArrayMessage.trim() : null;
  }

  if (errors && typeof errors === 'object') {
    for (const value of Object.values(errors as Record<string, unknown>)) {
      if (typeof value === 'string' && value.trim()) {
        return value.trim();
      }
      if (Array.isArray(value)) {
        const firstListMessage = value.find((item: unknown) => typeof item === 'string' && item.trim());
        if (typeof firstListMessage === 'string') {
          return firstListMessage.trim();
        }
      }
    }
  }

  return null;
}

function resolveDetails(body: any): string[] {
  if (Array.isArray(body?.details)) {
    return body.details
      .filter((item: unknown) => typeof item === 'string' && item.trim())
      .map((item: string) => item.trim());
  }
  return [];
}
